import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/cliente';
import type { Zona, ZonaAdmin } from '../api/tipos';
import { Aviso, Campo, Seleccion } from './piezas';

/**
 * Da de alta un barrio sin salir del formulario del inmueble.
 *
 * <p>La carga inicial de zonas trajo lo que hay dibujado en OpenStreetMap —trece
 * comunas y cuarenta y seis barrios de Ibagué— y eso no es todo lo que la gente
 * usa para decir dónde vive: hay barrios que no son oficiales, urbanizaciones
 * nuevas y sectores que solo existen en el habla.
 *
 * <p>Va aquí y no en una pantalla de administración aparte porque el momento en
 * que se descubre que falta un barrio es justo este: alguien cargando un
 * inmueble que no puede ubicar. Mandarle a otra pantalla, crear la zona y
 * volver a empezar el formulario es como se pierde media hora de trabajo.
 */
export default function CrearZona({
  ciudadId,
  zonas,
  alCrear,
}: {
  ciudadId: string;
  /** Las que ya hay, para ofrecer la comuna madre y avisar de duplicados. */
  zonas: Zona[];
  /** Se llama con la zona nueva ya creada, para dejarla seleccionada. */
  alCrear: (zona: Zona) => void;
}) {
  const cache = useQueryClient();
  const [abierto, setAbierto] = useState(false);
  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState('BARRIO');
  const [zonaPadreId, setZonaPadreId] = useState('');
  const [error, setError] = useState<unknown>(null);

  const comunas = zonas.filter((z) => z.tipo === 'COMUNA');

  const crear = useMutation({
    mutationFn: () =>
      api<ZonaAdmin>('/admin/geografia/zonas', {
        metodo: 'POST',
        cuerpo: {
          ciudadId,
          nombre: nombre.trim(),
          tipo,
          zonaPadreId: zonaPadreId || null,
          // Un barrio se encuadra más cerca que una comuna.
          zoomSugerido: tipo === 'COMUNA' ? 13 : 15,
        },
      }),
    onSuccess: (zona) => {
      setError(null);
      setNombre('');
      setZonaPadreId('');
      setAbierto(false);
      void cache.invalidateQueries({ queryKey: ['zonas'] });
      alCrear(zona);
    },
    onError: (e) => setError(e),
  });

  if (!abierto) {
    return (
      <button
        type="button"
        className="plano"
        style={{ marginTop: 6, fontSize: '0.82rem' }}
        onClick={() => setAbierto(true)}
      >
        + El barrio no está en la lista
      </button>
    );
  }

  const repetido = zonas.some(
    (z) => z.nombre.trim().toLowerCase() === nombre.trim().toLowerCase(),
  );

  return (
    <div
      style={{
        marginTop: 10,
        padding: 12,
        border: '1px solid var(--linea)',
        borderRadius: 8,
        background: 'var(--fondo)',
      }}
    >
      <Aviso error={error} />

      <Campo
        etiqueta="Nombre del barrio"
        valor={nombre}
        alCambiar={setNombre}
        ayuda="Como lo dice la gente. Se usará en el filtro del portal."
      />

      <div style={{ height: 8 }} />
      <Seleccion
        etiqueta="Tipo"
        valor={tipo}
        alCambiar={setTipo}
        opciones={[
          { valor: 'BARRIO', texto: 'Barrio' },
          { valor: 'SECTOR', texto: 'Sector' },
          { valor: 'COMUNA', texto: 'Comuna' },
          { valor: 'VEREDA', texto: 'Vereda' },
          { valor: 'CORREGIMIENTO', texto: 'Corregimiento' },
        ]}
      />

      {tipo !== 'COMUNA' && comunas.length > 0 && (
        <>
          <div style={{ height: 8 }} />
          <Seleccion
            etiqueta="Comuna a la que pertenece"
            valor={zonaPadreId}
            alCambiar={setZonaPadreId}
            vacio="No lo sé"
            ayuda="Opcional. Si no se sabe, se deja en blanco."
            opciones={comunas.map((c) => ({ valor: c.id, texto: c.nombre }))}
          />
        </>
      )}

      {repetido && nombre.trim() !== '' && (
        <p className="campo__ayuda" style={{ color: '#B77714' }}>
          Ya hay una zona con ese nombre. Revísala antes de crear otra.
        </p>
      )}

      <div className="fila" style={{ marginTop: 12 }}>
        <button
          type="button"
          className="secundario"
          disabled={nombre.trim() === '' || crear.isPending}
          onClick={() => crear.mutate()}
        >
          {crear.isPending ? 'Creando…' : 'Crear y seleccionar'}
        </button>
        <button type="button" className="plano" onClick={() => setAbierto(false)}>
          Cancelar
        </button>
      </div>
    </div>
  );
}
